// Per-event dashboard tile layout: mode + geometry, persisted in localStorage.
//
// Three modes (see DashboardTiles.tsx):
//   auto     — responsive 1–4 col grid, drag-to-reorder (the original behavior)
//   grid     — fixed cols×rows; tiles occupy explicit cells, gaps allowed
//   freeform — absolute pixel positions on a canvas, no overlap
//
// One key per window so different events keep different layouts. The legacy
// `dyno-tile-order:<window>` key (a bare string[]) is migrated into `autoOrder`
// the first time a window is loaded under the new schema.

export type LayoutMode = "auto" | "grid" | "freeform";

export interface GridCell {
  col: number; // 0-indexed
  row: number; // 0-indexed
}

export interface FreeformPos {
  x: number; // px from canvas left
  y: number; // px from canvas top
}

export interface DashboardLayout {
  mode: LayoutMode;
  /** auto mode: render order of system_ids */
  autoOrder: string[];
  /** grid mode: column count (1..MAX_COLS) */
  gridCols: number;
  /** grid mode: row count (1..MAX_ROWS) */
  gridRows: number;
  /** grid mode: explicit cell per system_id */
  gridCells: Record<string, GridCell>;
  /** freeform mode: explicit pixel position per system_id */
  freeform: Record<string, FreeformPos>;
}

export const MAX_COLS = 10;
export const MAX_ROWS = 12;

/** Fixed tile width on the freeform canvas. Heights stay content-driven. */
export const FREEFORM_TILE_W = 300;
/** Assumed tile height used only for placing brand-new tiles before measure. */
export const FREEFORM_TILE_H_GUESS = 280;
/** Gutter between auto-placed freeform tiles. */
export const FREEFORM_GAP = 16;

export function defaultLayout(): DashboardLayout {
  return {
    mode: "auto",
    autoOrder: [],
    gridCols: 4,
    gridRows: 3,
    gridCells: {},
    freeform: {},
  };
}

const KEY = (windowId: string) => `dyno-dashboard-layout:${windowId}`;
const LEGACY_ORDER_KEY = (windowId: string) => `dyno-tile-order:${windowId}`;

function clampInt(v: unknown, lo: number, hi: number, fallback: number): number {
  const n = typeof v === "number" ? Math.round(v) : NaN;
  if (!Number.isFinite(n)) return fallback;
  return Math.min(hi, Math.max(lo, n));
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

function normalize(raw: unknown): DashboardLayout {
  const d = defaultLayout();
  if (!isPlainObject(raw)) return d;
  const mode =
    raw.mode === "grid" || raw.mode === "freeform" || raw.mode === "auto"
      ? raw.mode
      : "auto";
  const gridCells: Record<string, GridCell> = {};
  if (isPlainObject(raw.gridCells)) {
    for (const [id, c] of Object.entries(raw.gridCells)) {
      if (isPlainObject(c) && typeof c.col === "number" && typeof c.row === "number") {
        gridCells[id] = { col: Math.round(c.col), row: Math.round(c.row) };
      }
    }
  }
  const freeform: Record<string, FreeformPos> = {};
  if (isPlainObject(raw.freeform)) {
    for (const [id, p] of Object.entries(raw.freeform)) {
      if (isPlainObject(p) && typeof p.x === "number" && typeof p.y === "number") {
        freeform[id] = { x: Math.max(0, p.x), y: Math.max(0, p.y) };
      }
    }
  }
  return {
    mode,
    autoOrder: Array.isArray(raw.autoOrder) ? raw.autoOrder.map(String) : [],
    gridCols: clampInt(raw.gridCols, 1, MAX_COLS, d.gridCols),
    gridRows: clampInt(raw.gridRows, 1, MAX_ROWS, d.gridRows),
    gridCells,
    freeform,
  };
}

export function loadLayout(windowId: string): DashboardLayout {
  try {
    const raw = localStorage.getItem(KEY(windowId));
    if (raw) return normalize(JSON.parse(raw));
    // Migrate the legacy bare-order key, if present.
    const legacy = localStorage.getItem(LEGACY_ORDER_KEY(windowId));
    if (legacy) {
      const arr = JSON.parse(legacy);
      if (Array.isArray(arr)) {
        return { ...defaultLayout(), autoOrder: arr.map(String) };
      }
    }
  } catch {
    /* corrupt / disabled storage — fall through to default */
  }
  return defaultLayout();
}

export function saveLayout(windowId: string, layout: DashboardLayout): void {
  try {
    localStorage.setItem(KEY(windowId), JSON.stringify(layout));
  } catch {
    /* localStorage full / disabled — in-memory layout still works */
  }
}

/**
 * Resolve grid placement for the given service ids against a fixed cols×rows
 * grid. Saved cells are honored when in-bounds and non-colliding; everything
 * else is auto-filled row-major into free cells. Services that don't fit go
 * into `overflow` (caller renders them in a strip below).
 */
export function resolveGrid(
  layout: Pick<DashboardLayout, "gridCols" | "gridRows" | "gridCells">,
  ids: string[],
): { placed: Record<string, GridCell>; overflow: string[] } {
  const { gridCols: cols, gridRows: rows } = layout;
  const occupied = new Set<string>();
  const placed: Record<string, GridCell> = {};
  const cellKey = (c: number, r: number) => `${c},${r}`;

  for (const id of ids) {
    const c = layout.gridCells[id];
    if (!c) continue;
    if (c.col < 0 || c.col >= cols || c.row < 0 || c.row >= rows) continue;
    const k = cellKey(c.col, c.row);
    if (occupied.has(k)) continue; // collision — defer to auto-fill
    occupied.add(k);
    placed[id] = { col: c.col, row: c.row };
  }

  const overflow: string[] = [];
  let scan = 0;
  for (const id of ids) {
    if (placed[id]) continue;
    let found = false;
    while (scan < cols * rows) {
      const col = scan % cols;
      const row = Math.floor(scan / cols);
      scan++;
      if (!occupied.has(cellKey(col, row))) {
        occupied.add(cellKey(col, row));
        placed[id] = { col, row };
        found = true;
        break;
      }
    }
    if (!found) overflow.push(id);
  }
  return { placed, overflow };
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Axis-aligned overlap test. Touching edges (shared border) do not count. */
export function rectsOverlap(a: Rect, b: Rect): boolean {
  return (
    a.x < b.x + b.w &&
    a.x + a.w > b.x &&
    a.y < b.y + b.h &&
    a.y + a.h > b.y
  );
}

/**
 * Resolve overlaps on the freeform canvas after a tile is dropped.
 *
 * The dragged tile (`anchorId`) is immovable — it stays exactly where the user
 * dropped it. Every other tile it (transitively) overlaps is nudged out of the
 * way: pushed below the tile it collides with, or above when there's room and
 * the tile already sits higher. Cascades until nothing overlaps, so you can
 * drop a tile sloppily between others and they part to make room.
 *
 * Pure function — `positions` is not mutated. `heights` maps system_id to the
 * tile's measured pixel height (caller supplies; falls back to a guess).
 */
export function resolveFreeformCollisions(
  positions: Record<string, FreeformPos>,
  heights: Record<string, number>,
  anchorId: string,
): Record<string, FreeformPos> {
  const ids = Object.keys(positions);
  const next: Record<string, FreeformPos> = {};
  for (const id of ids) next[id] = { ...positions[id] };

  const h = (id: string) => heights[id] || FREEFORM_TILE_H_GUESS;
  const rectOf = (id: string): Rect => ({
    x: next[id].x,
    y: next[id].y,
    w: FREEFORM_TILE_W,
    h: h(id),
  });

  // Iterative relaxation. Each pass separates one overlapping pair; the cap
  // guarantees termination even in pathological arrangements.
  for (let iter = 0; iter < 400; iter++) {
    let moved = false;
    for (let a = 0; a < ids.length; a++) {
      for (let b = a + 1; b < ids.length; b++) {
        const ra = rectOf(ids[a]);
        const rb = rectOf(ids[b]);
        if (!rectsOverlap(ra, rb)) continue;

        // The anchor never moves; otherwise the lower tile yields.
        let moverId: string;
        let fixed: Rect;
        if (ids[a] === anchorId) {
          moverId = ids[b];
          fixed = ra;
        } else if (ids[b] === anchorId) {
          moverId = ids[a];
          fixed = rb;
        } else if (ra.y + ra.h / 2 >= rb.y + rb.h / 2) {
          moverId = ids[a];
          fixed = rb;
        } else {
          moverId = ids[b];
          fixed = ra;
        }

        const mover = rectOf(moverId);
        const moverCenterY = mover.y + mover.h / 2;
        const fixedCenterY = fixed.y + fixed.h / 2;
        let newY: number;
        if (moverCenterY >= fixedCenterY) {
          newY = fixed.y + fixed.h + FREEFORM_GAP; // push below
        } else {
          newY = fixed.y - mover.h - FREEFORM_GAP; // push above
          if (newY < 0) newY = fixed.y + fixed.h + FREEFORM_GAP; // no room up
        }
        next[moverId] = { x: next[moverId].x, y: newY };
        moved = true;
      }
    }
    if (!moved) break;
  }
  return next;
}

/**
 * Find a non-overlapping freeform slot for a new tile, scanning row-major over
 * a virtual grid sized to `canvasWidth`. `occupied` is the list of existing
 * tile rects. Returns a position guaranteed not to overlap them.
 */
export function findFreeformSlot(
  occupied: Rect[],
  canvasWidth: number,
  tileH: number = FREEFORM_TILE_H_GUESS,
): FreeformPos {
  const stepX = FREEFORM_TILE_W + FREEFORM_GAP;
  const stepY = tileH + FREEFORM_GAP;
  const colsPerRow = Math.max(1, Math.floor((canvasWidth + FREEFORM_GAP) / stepX));
  for (let i = 0; i < 1000; i++) {
    const x = (i % colsPerRow) * stepX;
    const y = Math.floor(i / colsPerRow) * stepY;
    const candidate: Rect = { x, y, w: FREEFORM_TILE_W, h: tileH };
    if (!occupied.some((r) => rectsOverlap(candidate, r))) {
      return { x, y };
    }
  }
  return { x: 0, y: 0 };
}
